import type { ExerciseSetValues } from "../shared/set-values";
import type {
  ActiveSession,
  LoadMode,
  TemplateExercise,
} from "../shared/types";

export interface SetDraft {
  weight: string;
  reps: string;
  count: string;
}

export type SetDraftField = keyof SetDraft;
export type SetDrafts = Record<string, SetDraft>;
export type SetDraftTouches = Partial<Record<SetDraftField, boolean>>;

export function setDraftKey(
  sessionId: string,
  templateExerciseId: string,
  setNumber: number,
  exerciseId: string,
) {
  return `${sessionId}:${templateExerciseId}:${exerciseId}:${setNumber}`;
}

export function initialSetDraft(
  session: ActiveSession,
  item: TemplateExercise,
): SetDraft {
  const today = [...session.sets]
    .reverse()
    .find((set) => set.exerciseId === item.exercise.id);
  const previous = today?.weightValue ?? item.lastPerformance?.weightValue;
  return {
    weight: previous === null || previous === undefined ? "" : String(previous),
    reps: "",
    count: item.targetCount ? String(item.targetCount) : "",
  };
}

/**
 * A draft is immutable for its session/exercise/set slot once created. Later
 * hydration or pointer reconciliation can supply defaults for a new slot, but
 * can never overwrite a value the user already entered in this slot.
 */
export function updateSetDraft(
  drafts: SetDrafts,
  key: string,
  defaults: SetDraft,
  field: SetDraftField,
  value: string,
): SetDrafts {
  return {
    ...drafts,
    [key]: { ...(drafts[key] ?? defaults), [field]: value },
  };
}

export function ensureSetDraft(
  drafts: SetDrafts,
  key: string,
  defaults: SetDraft,
): SetDrafts {
  return drafts[key] ? drafts : { ...drafts, [key]: defaults };
}

/**
 * Moving between untouched exercises is navigation, not draft persistence.
 * Discard both the exercise being left and any earlier draft for the exercise
 * being entered, then seed the selected slot from its current canonical
 * history. Re-selecting the same live slot is deliberately a no-op so a rapid
 * input event cannot be erased by a redundant click or render.
 */
export function switchSetDraftSelection(
  drafts: SetDrafts,
  previousKey: string | null,
  nextKey: string,
  defaults: SetDraft,
): SetDrafts {
  if (previousKey === nextKey) return ensureSetDraft(drafts, nextKey, defaults);
  const next = { ...drafts };
  if (previousKey) delete next[previousKey];
  delete next[nextKey];
  next[nextKey] = defaults;
  return next;
}

function formString(data: Pick<FormData, "get">, name: string) {
  const value = data.get(name);
  return typeof value === "string" ? value.trim() : "";
}

/** Reads the browser's submit-time values, including autofill/programmatic edits. */
export function setDraftFromFormData(data: Pick<FormData, "get">): SetDraft {
  return {
    weight: formString(data, "weight"),
    reps: formString(data, "reps"),
    count: formString(data, "count"),
  };
}

/**
 * Prefer the synchronous input-event snapshot for fields the user touched.
 * This protects a newer keystroke from a later WebKit/React DOM reset while
 * retaining FormData support for autofill and programmatic values that do not
 * dispatch an input event. An intentional user clear is a touched empty value.
 */
export function mergeSubmitDraft(
  formDraft: SetDraft,
  eventDraft: SetDraft,
  touched: SetDraftTouches,
): SetDraft {
  return {
    weight: touched.weight ? eventDraft.weight : formDraft.weight,
    reps: touched.reps ? eventDraft.reps : formDraft.reps,
    count: touched.count ? eventDraft.count : formDraft.count,
  };
}

function numberOrNull(value: string) {
  return value === "" ? null : Number(value);
}

export function setValuesFromDraft(
  loadMode: LoadMode,
  draft: SetDraft,
): ExerciseSetValues {
  if (loadMode === "count")
    return {
      weightValue: null,
      reps: null,
      countValue: numberOrNull(draft.count),
    };
  return {
    weightValue:
      loadMode === "bodyweight"
        ? (numberOrNull(draft.weight) ?? 0)
        : numberOrNull(draft.weight),
    reps: numberOrNull(draft.reps),
    countValue: null,
  };
}
